// Bella Vita frontend — BACKEND ADAPTER CONTRACT
// =============================================================================
// Every adapter (mock today, Odoo later) implements this exact surface. The
// service layer (src/services/*) is the only code that imports an adapter, and
// UI code imports only the service layer. Swapping MockAdapter -> OdooAdapter
// must not require any UI change.
//
// All methods are async and may throw. Thrown errors surface to the UI as
// loading/error/retry states — never as an unhandled rejection.
//
// Shapes (localised fields are { ar, en } objects):
//   Category    { id, name }
//   Service     { id, cat, name, price, dur, img }
//   Specialist  { id, name, initials, anyPreference? }
//   DayAvail    { dateISO, open, hasSlots }
//   Slot        { group:'morning'|'afternoon'|'evening', time:'HH:MM', available }
//   BookingReq  { partySize, picks:[{personId,serviceId}], specialistId,
//                 dateISO, time, customer:{name,phone,email,notes},
//                 paymentMethod, agreedTerms, referralCode?, idempotencyKey? }
//   Booking     { ref, dateISO, time, status, specialist, services:[{name,price}],
//                 total, deposit, depositPaid }
//   Session     { token, customer:{ id, name, phone, email } }
//
// NOTE: no `branch` / `location` field anywhere — Bella Vita is one salon.
// =============================================================================

/**
 * @typedef {Object} BellaVitaAdapter
 *
 * @property {() => Promise<{vatRate:number,depositRate:number,currency:string}>} getPricing
 *            (tax + deposit config for the wizard's live price preview; the
 *             authoritative totals still come back from createBooking())
 * @property {() => Promise<Category[]>}                         getCategories
 * @property {() => Promise<Service[]>}                          getServices
 * @property {(id:string) => Promise<Service|null>}             getService
 * @property {(opts?:{ serviceIds?:string[] }) => Promise<Specialist[]>} getSpecialists
 * @property {() => Promise<{id:string,name:object,marks:string[],backendMethod?:string}[]>} getPaymentMethods
 *            (server-eligible methods shown in customer checkout)
 * @property {(opts:{bookingRef:string,phone:string,method:string}) => Promise<object>} startPayment
 * @property {(reference:string,opts:{bookingRef:string,phone:string}) => Promise<object>} getPaymentStatus
 * @property {(reference:string,opts:{bookingRef:string,phone:string,paymentId:string}) => Promise<object>} bindMoyasarPayment
 *            (serviceIds — when supplied, restrict to specialists able to perform
 *             ALL of those services. The mock ignores it; Odoo will honour it.)
 *
 * @property {(opts:{ monthISO:string, specialistId?:string, serviceIds?:string[] }) => Promise<DayAvail[]>} getAvailability
 * @property {(opts:{ dateISO:string, specialistId?:string, serviceIds?:string[] }) => Promise<Slot[]>} getSlots
 * @property {() => Promise<{days:number, firstDateISO:string, lastDateISO:string, tz:string}>} getBookingWindow
 *            (LOCKED 7-day customer horizon; the frontend must not book past it)
 * @property {(opts:{ serviceIds?:string[], specialistId?:string, durationMinutes?:number }) => Promise<{dateISO:string,time:string,group:string}|null>} getNextAvailable
 *
 * @property {(opts:{code:string,serviceIds:string[]}) => Promise<object>} quoteBookingDiscount
 * @property {(req:BookingReq) => Promise<Booking>}              createBooking
 * @property {(ref:string) => Promise<Booking|null>}            getBooking
 * @property {(opts?:{ scope?:'all'|'upcoming'|'past' }) => Promise<Booking[]>} getMyBookings
 * @property {(ref:string, patch:Partial<BookingReq>) => Promise<Booking>} rescheduleBooking
 * @property {(ref:string) => Promise<{ ref:string, status:'cancelled' }>} cancelBooking
 *
 * @property {(opts:{ phone:string, purpose:'register'|'login'|'reset' }) => Promise<{ sent:true, expiresInSec:number }>} requestOtp
 * @property {(opts:{ phone:string, code:string }) => Promise<Session>}  verifyOtp
 * @property {(opts:{ email:string, password:string }) => Promise<Session>} login
 * @property {() => Promise<void>}                               logout
 * @property {() => Promise<Session|null>}                       currentSession
 */

export const ADAPTER_METHODS = [
  'getPricing',
  'getCategories', 'getServices', 'getService', 'getSpecialists', 'getPaymentMethods', 'startPayment', 'getPaymentStatus', 'bindMoyasarPayment',
  'getAvailability', 'getSlots', 'getBookingWindow', 'getNextAvailable',
  'quoteBookingDiscount', 'createBooking', 'getBooking', 'getMyBookings', 'rescheduleBooking', 'cancelBooking',
  'requestOtp', 'verifyOtp', 'login', 'logout', 'currentSession',
];

/** Throws if `adapter` is missing any contract method. Used at boot. */
export function assertAdapter(adapter, label) {
  const missing = ADAPTER_METHODS.filter((m) => typeof adapter?.[m] !== 'function');
  if (missing.length) {
    throw new Error(`[${label}] adapter is missing methods: ${missing.join(', ')}`);
  }
}
